"use client";
import Banner from "../shared/Banner";
import BannerPlaceholder from "../shared/BannerPlaceholder";
import { chunkData } from "@/app/utils/helperFunctions";
import useMediaGetter from "@/app/hooks/useMediaGetter";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { loadingContext } from "@/app/store/context/isLoadingContext";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import dynamic from "next/dynamic";

const AmenitiesList = dynamic(() => import("./AmenitiesList"), {
	loading: () => <div className="min-h-[20rem]" aria-hidden />,
});

const MasterPlan = dynamic(() => import("../neighborhoods/MasterPlan"), {
	ssr: false,
	loading: () => (
		<div className="min-h-[62svh] lg:min-h-screen bg-oBlue-100" aria-hidden />
	),
});

interface Facility {
	id: number;
	name: string;
	type: "facility" | string;
}

const AmenitiesPageComponent = ({
	facilitiesData,
	banner,
	initialBannerImageSrc,
}: any) => {
	const { isIpad } = useContext(screenSizeContext);
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext);
	const { imgSrc, videoSrc } = useMediaGetter(banner?.banners?.[0]?.media);
	const bannerImageSrc = imgSrc ?? initialBannerImageSrc;

	const facilities = useMemo<Facility[]>(
		() =>
			facilitiesData?.children?.filter(
				(item: Facility): item is Facility => item.type === "facility"
			) ?? [],
		[facilitiesData?.children]
	);

	const amenitiesChunks = useMemo(
		() => chunkData(facilities, 7),
		[facilities]
	);

	const [selectedAmenityScroll, setSelectedAmenityScroll] = useState<
		string | number | null
	>(null);

	useEffect(() => {
		if (bannerImageSrc) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [bannerImageSrc, setIsPageFetched]);

	useEffect(() => {
		if (facilities.length > 0) {
			setTimeout(() => {
				setIsPageFetched((prev: string) => {
					if (prev === "loading") return "fetched";
					return prev;
				});
			});
		}
	}, [facilities.length, setIsPageFetched]);

	const tl = useRef<GSAPTimeline | null>(null);

	useEffect(() => {
		if (isPageFetched === "fetched" && bannerImageSrc) {
			let cancelled = false;

			const timer = setTimeout(async () => {
				const { default: gsap } = await import("gsap");
				if (cancelled) return;

				const html = document.documentElement;

				tl.current?.kill();
				tl.current = gsap.timeline();
				tl.current
					.timeScale(html.classList.contains("loaded") ? 1.25 : 1)
					.set(".banner .hero-title-word, .banner-head-desc", {
						yPercent: 50,
						alpha: 0,
					})
					.set(".banner .transparent-o", { yPercent: 30 })
					.set(".banner .hero-video", {
						clipPath: "inset(50% 50% 50% 50%)",
						scale: 0.75,
						transformOrigin: "center center",
					})
					.to(".banner .hero-image img", {
						delay: html.classList.contains("loaded") ? 0.8 : 1.2,
						duration: 1,
						ease: "power2.inOut",
						y: "0%",
					})
					.to(
						".banner .hero-image-wrapper",
						{
							duration: 1,
							ease: "power2.inOut",
							scale: 1,
						},
						"-=0.25"
					)
					.to(
						".navbar_section",
						{
							duration: 1,
							ease: "power2.inOut",
							y: "0%",
						},
						"-=0.4"
					)
					.to(
						".banner .hero-video",
						{
							duration: 1,
							ease: "expo.inOut",
							scale: 1,
							clipPath: "inset(0% 0% 0% 0%)",
						},
						"<"
					)
					.to(
						".banner .hero-title-word, .banner-head-desc",
						{
							duration: 1,
							stagger: 0.05,
							ease: "expo.inOut",
							yPercent: 0,
							alpha: 1,
						},
						"<"
					)
					.to(
						".banner .transparent-o",
						{
							duration: 1,
							stagger: 0.05,
							ease: "expo.out",
							yPercent: 0,
						},
						"<"
					);
			}, 50);

			return () => {
				cancelled = true;
				clearTimeout(timer);
				tl.current?.kill();
			};
		}
	}, [isPageFetched, bannerImageSrc]);

	return (
		<div className={`page_wrapper ${isIpad && "amenities-page"}`}>
			{bannerImageSrc ? (
				<Banner
					priority
					imageSrc={`${bannerImageSrc}`}
					title={banner?.banners?.[0]?.title?.en}
					minHeight="89vh"
					videoSrc={videoSrc || "/owest-banner.mp4"}
					description={banner?.banners?.[0]?.description?.en}
					optimizedImage
				/>
			) : (
				<BannerPlaceholder minHeight="89vh" />
			)}
			<div className="amenities-content px lg:py-[6.5rem] py-16 pb-0 bg-white min-h-[20rem]">
				{amenitiesChunks?.map((data, index) => (
					<AmenitiesList
						data={data}
						key={index}
						setSelectedAmenityScroll={setSelectedAmenityScroll}
					/>
				))}
			</div>
			<MasterPlan
				amenitiesPage
				data={facilitiesData}
				title={facilitiesData?.title}
				activeAmenity={selectedAmenityScroll}
				optimizedImage
			/>
		</div>
	);
};

export default AmenitiesPageComponent;
